import { observer } from "mobx-react";
import React from "react";
import styled from "styled-components";

import DashboardBarChart from "@client/components/modules/DashboardModules/DashboardBarChart";
import DashboardEmpty from "@client/components/modules/DashboardModules/DashboardEmpty";
import DashboardInfoCount from "@client/components/modules/DashboardModules/DashboardInfoCount";
import DashboardLicencesSoonToExpire from "@client/components/modules/DashboardModules/DashboardLicencesSoonToExpire";
import DashboardLineChart from "@client/components/modules/DashboardModules/DashboardLineChart";
import DashboardPieChart from "@client/components/modules/DashboardModules/DashboardPieChart";
import DashboardTitle from "@client/components/modules/DashboardModules/DashboardTitle";
import DashboardUtils from "@client/components/modules/DashboardModules/DashboardUtils";
import customerImage from "@client/components/ui/sharedImages/customer.svg";
import licenceImage from "@client/components/ui/sharedImages/licence.svg";
import partnerImage from "@client/components/ui/sharedImages/partner.svg";
import { isLocationAvailable } from "@client/utils/Constants";
import Style from "@client/utils/Style";
import { Customer } from "@typings/Customer";
import { TokenInfo } from "@typings/Identity";
import { Licence } from "@typings/Licence";
import { Licensor } from "@typings/Licensor";
import { Partner } from "@typings/Partner";
import { Tag } from "@typings/Tag";

const Wrapper = styled.div`
  > div {
    margin-bottom: 32px;
  }
`;
const RowLayout = styled.div`
  display: flex;
  margin-left: -32px;
  > div {
    margin-left: 32px;
    flex: 1;
    padding: 8px;
    min-width: 224px;
  }
`;

type State = {
  totalLicencesDate: Date | null;
  replicasMigrationsDate: Date | null;
  partnersLicencesDate: Date | null;
};
type Props = {
  tags: Tag[];
  licences: Licence[];
  customers: Customer[];
  licensors: Licensor[];
  partnersCustomers: Customer[];
  partnersLicences: Licence[];
  licencesLoading: boolean;
  customersLoading: boolean;
  licensorsLoading: boolean;
  tagsLoading: boolean;
  tokenInfo: TokenInfo | null;
  partners: Partner[];
  partnersLoading: boolean;
  onNewLicenceClick: () => void;
  onNewCustomerClick: () => void;
  onNewPartnerClick: () => void;
};
@observer
class DashboardContent extends React.Component<Props, State> {
  state: State = {
    totalLicencesDate: null,
    replicasMigrationsDate: null,
    partnersLicencesDate: null,
  };

  renderLicenceEmpty() {
    return (
      <DashboardEmpty
        image={licenceImage}
        message="You don't have any created licences."
        buttonText="Create Licence"
        onButtonClick={this.props.onNewLicenceClick}
      />
    );
  }

  renderCustomersEmpty() {
    return (
      <DashboardEmpty
        image={customerImage}
        message="You don't have any created customers."
        buttonText="Create Customer"
        onButtonClick={this.props.onNewCustomerClick}
      />
    );
  }

  renderPartnersEmpty() {
    return (
      <DashboardEmpty
        image={partnerImage}
        message="You don't have any created partners or the partners you have created don't have any licences."
        buttonText="Create Partner"
        onButtonClick={this.props.onNewPartnerClick}
      />
    );
  }

  render() {
    const infoCountData = [
      {
        label: "Licences",
        value: this.props.licences.length,
        color: Style.palette.alert,
        link: "/licences",
        loading: this.props.licencesLoading,
      },
      {
        label: "Customers",
        value: this.props.customers.length,
        color: Style.palette.primary,
        link: "/customers",
        loading: this.props.customersLoading,
      },
      {
        label: "Licensors",
        value: this.props.licensors.length || null,
        color: Style.palette.black,
        link: this.props.licensors.length ? "/licensors" : null,
        loading: this.props.licensorsLoading,
      },
    ];

    if (isLocationAvailable("/partners", this.props.tokenInfo)) {
      infoCountData.push({
        label: "Partners",
        value: this.props.partners.length,
        color: Style.palette.black,
        link: "/partners",
        loading: this.props.partnersLoading,
      });
    }

    return (
      <Wrapper>
        <DashboardInfoCount data={infoCountData} />
        <RowLayout>
          <DashboardLicencesSoonToExpire
            tags={this.props.tags}
            licences={this.props.licences}
            style={{ padding: 0 }}
            emptyComponent={this.renderLicenceEmpty()}
          />
          <DashboardPieChart
            title="Licences by Tags"
            data={DashboardUtils.calculateLicencesChart(
              this.props.licences,
              this.props.tags,
            )}
            emptyComponent={this.renderLicenceEmpty()}
          />
          <DashboardPieChart
            title="Licences by Customers"
            data={DashboardUtils.calculateCustomersChart(
              this.props.licences,
              this.props.customers,
            )}
            emptyComponent={this.renderCustomersEmpty()}
          />
        </RowLayout>
        <RowLayout>
          <DashboardLineChart
            title={
              <DashboardTitle
                title="Total Licences"
                date={this.state.totalLicencesDate}
                onChange={date => {
                  this.setState({ totalLicencesDate: date });
                }}
              />
            }
            data={DashboardUtils.calculateLicenceCreatedChart(
              this.props.licences,
              this.state.totalLicencesDate,
            )}
            style={{ minWidth: "480px" }}
            emptyComponent={this.renderLicenceEmpty()}
          />
          <DashboardBarChart
            title={
              <DashboardTitle
                title="Replicas / Migrations"
                date={this.state.replicasMigrationsDate}
                onChange={date => {
                  this.setState({ replicasMigrationsDate: date });
                }}
              />
            }
            data={DashboardUtils.calculateReplMigrCreatedChart(
              this.props.licences,
              this.state.replicasMigrationsDate,
            )}
            style={{ minWidth: "480px" }}
            emptyComponent={this.renderLicenceEmpty()}
          />
        </RowLayout>
        {isLocationAvailable("/partners", this.props.tokenInfo) ? (
          <RowLayout>
            <DashboardLineChart
              title={
                <DashboardTitle
                  title="Total Partner Licences"
                  date={this.state.partnersLicencesDate}
                  onChange={date => {
                    this.setState({ partnersLicencesDate: date });
                  }}
                />
              }
              data={DashboardUtils.calculatePartnerLicencesCreatedChart(
                this.props.partnersLicences,
                this.props.partnersCustomers,
                this.props.partners,
                this.state.partnersLicencesDate,
              )}
              style={{ minWidth: "480px" }}
              emptyComponent={this.renderPartnersEmpty()}
            />
            <DashboardPieChart
              title="Licences by Partners"
              data={DashboardUtils.calculatePartnersChart(
                this.props.partnersLicences,
                this.props.partnersCustomers,
                this.props.partners,
              )}
              emptyComponent={this.renderPartnersEmpty()}
            />
          </RowLayout>
        ) : null}
      </Wrapper>
    );
  }
}

export default DashboardContent;
